import React, {useState, useEffect} from 'react';
import {Box, Typography, CircularProgress} from '@mui/material';
import {styled} from '@mui/material/styles';
import {ChevronRight} from '@mui/icons-material';
import {IAgoraRTCRemoteUser} from 'agora-rtc-sdk-ng';

import {EnhancedConnectionState} from '../types/EnhancedConnectionState';

const Container = styled(Box)(({theme}) => ({
  position: 'fixed',
  height: '150px',
  zIndex: 50,
  overflow: 'visible',
}));

const VideoContainer = styled(Box)(({theme}) => ({
  position: 'absolute',
  width: '120px',
  height: '150px',
  borderRadius: '10px',
  overflow: 'hidden',
  marginLeft: theme.spacing(2),
  boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
  cursor: 'pointer',
  transition: 'transform 0.3s ease',
}));

const CollapsedButton = styled(Box)(({theme}) => ({
  position: 'absolute',
  width: '50px',
  height: '40px',
  background: '#5374FC85',
  borderRadius: '0 30px 30px 0',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  boxShadow: '4px 0 8px rgba(0, 0, 0, 0.2)',
  cursor: 'pointer',
  color: 'white',
  transition: 'transform 0.3s ease',
}));

const StatusContainer = styled(Box)(({theme}) => ({
  width: '100%',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  backgroundColor: '#444',
  color: '#fff',
  gap: theme.spacing(1),
}));

interface CollapsibleVideoProps {
  position: 'top' | 'bottom';
  backgroundColor: string;
  isCollapsed: boolean;
  setIsCollapsed: (collapsed: boolean) => void;
  children: React.ReactNode;
}

export const CollapsibleVideo: React.FC<CollapsibleVideoProps> = ({
  position,
  backgroundColor,
  isCollapsed,
  setIsCollapsed,
  children,
}) => {
  const handleToggle = () => {
    setIsCollapsed(!isCollapsed);
  };

  const positionStyle = position === 'top' ? {top: '1rem'} : {bottom: '5rem'};

  return (
    <Container sx={{left: 0, ...positionStyle}}>
      <VideoContainer
        sx={{
          backgroundColor,
          transform: isCollapsed ? 'translateX(-120%)' : 'translateX(0)',
        }}
        onClick={handleToggle}
      >
        {children}
      </VideoContainer>

      <CollapsedButton
        sx={{
          left: '0',
          top: '55px',
          transform: isCollapsed ? 'translateX(0)' : 'translateX(-130px)',
        }}
        onClick={handleToggle}
      >
        <ChevronRight />
      </CollapsedButton>
    </Container>
  );
};

interface RemoteVideoContentProps {
  connectionState: EnhancedConnectionState;
  remoteUsers: IAgoraRTCRemoteUser[];
  isHost: boolean;
  reconnectAttempts?: number;
  connectionError?: Error | null;
}

export const RemoteVideoContent: React.FC<RemoteVideoContentProps> = ({
  connectionState,
  remoteUsers,
  isHost,
  reconnectAttempts = 0,
  connectionError = null,
}) => {
  // Show appropriate status messages based on connection state
  if (connectionState === 'INITIALIZING') {
    return (
      <StatusContainer>
        <CircularProgress size={24} color="inherit" />
        <Typography variant="caption">Initializing</Typography>
      </StatusContainer>
    );
  }

  if (connectionState === 'CONNECTING' || connectionState === 'RECONNECTING') {
    return (
      <StatusContainer>
        <CircularProgress size={24} color="inherit" />
        <Typography variant="caption">
          {reconnectAttempts > 0
            ? `Reconnecting (${reconnectAttempts})`
            : 'Connecting...'}
        </Typography>
      </StatusContainer>
    );
  }

  if (connectionState === 'PERMISSION_DENIED') {
    return (
      <StatusContainer>
        <Typography variant="caption">Camera/Mic Permission Denied</Typography>
      </StatusContainer>
    );
  }

  if (connectionState === 'ERROR' || connectionError) {
    return (
      <StatusContainer>
        <Typography variant="caption">
          {connectionError?.message || 'Connection Error'}
        </Typography>
      </StatusContainer>
    );
  }

  if (connectionState !== 'CONNECTED') {
    return (
      <StatusContainer>
        <Typography variant="caption">Not Connected</Typography>
      </StatusContainer>
    );
  }

  if (remoteUsers.length === 0) {
    return (
      <StatusContainer>
        <Typography variant="caption">
          {isHost ? 'Waiting on Viewer...' : 'Waiting on Agent...'}
        </Typography>
      </StatusContainer>
    );
  }

  return (
    <>
      {remoteUsers.map((user) => (
        <Box
          key={user.uid}
          id={`remote-video-${user.uid}`}
          sx={{
            width: '100%',
            height: '100%',
          }}
        />
      ))}
    </>
  );
};

interface LocalVideoContentProps {
  isVideoActive?: boolean;
  mediaPermissions?: {
    video: boolean;
    audio: boolean;
  };
}

export const LocalVideoContent: React.FC<LocalVideoContentProps> = ({
  isVideoActive = false,
  mediaPermissions = {video: true, audio: true},
}) => {
  return (
    <Box sx={{width: '100%', height: '100%', position: 'relative'}}>
      <Box
        id="local-video"
        sx={{
          width: '100%',
          height: '100%',
          position: 'absolute',
          top: 0,
          left: 0,
        }}
      />
      <Box
        sx={{
          width: '100%',
          height: '100%',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          position: 'absolute',
          top: 0,
          left: 0,
          zIndex: -1,
          backgroundColor: '#444',
        }}
      >
        <Typography variant="caption" sx={{color: '#fff'}}>
          {!mediaPermissions.video
            ? 'Camera Permission Denied'
            : !isVideoActive
            ? 'Camera Off'
            : ''}
        </Typography>
      </Box>
    </Box>
  );
};
